
import { lazy, Suspense } from "react";
import { Header } from "@/components/Header";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy load components with explicit chunk names and preload hints
const Hero = lazy(() => import(/* webpackChunkName: "hero", webpackPrefetch: true */ "@/components/Hero"));
const AgentCard = lazy(() => import(/* webpackChunkName: "agent-card" */ "@/components/AgentCard"));
const Footer = lazy(() => import(/* webpackChunkName: "footer" */ "@/components/Footer"));

// Optimized image URLs with dynamic sizing and WebP format
const agents = [
  {
    title: "Data Analyst Agent",
    description: "Process and analyze data with AI-powered insights",
    price: "$150+/Month",
    icon: "https://images.unsplash.com/photo-1649972904349-6e44c42644a7?auto=format&fit=crop&w=300&q=70&fm=webp",
    alt: "AI data analysis visualization showing machine learning insights"
  },
  {
    title: "Content Writer Agent",
    description: "Generate engaging content for your brand",
    price: "$150+/Month",
    icon: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=300&q=70&fm=webp",
    alt: "AI content generation and writing automation tools"
  },
  {
    title: "Research Assistant",
    description: "Accelerate your research with AI assistance",
    price: "$150+/Month",
    icon: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=300&q=70&fm=webp",
    alt: "AI-powered research assistance and data processing"
  }
];

// Loading fallback components optimized for performance
const HeroSkeleton = () => (
  <div className="min-h-[50vh] bg-gradient-to-br from-primary/50 to-accent/50">
    <Skeleton className="h-96 w-full" />
  </div>
);

const AgentCardSkeleton = () => (
  <div className="p-6 rounded-lg border-2">
    <Skeleton className="h-24 w-24 rounded-full mx-auto mb-4" />
    <Skeleton className="h-6 w-3/4 mx-auto mb-4" />
    <Skeleton className="h-4 w-full mb-2" />
    <Skeleton className="h-4 w-2/3 mx-auto" />
  </div>
);

const Index = () => {
  return (
    <div className="min-h-screen">
      <Header />
      
      <Suspense fallback={<HeroSkeleton />}>
        <Hero />
      </Suspense>
      
      <section id="agents" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Featured AI Agents for Business Automation
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Suspense 
              fallback={
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {[...Array(3)].map((_, i) => (
                    <AgentCardSkeleton key={i} />
                  ))}
                </div>
              }
            >
              {agents.map((agent) => (
                <AgentCard key={agent.title} {...agent} />
              ))}
            </Suspense>
          </div>
        </div>
      </section>

      <section id="features" className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Why Choose DubCity AI Solutions?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              {
                title: "Customizable AI Solutions",
                description: "Tailor each AI agent to your specific business needs and workflows"
              },
              {
                title: "Scalable Automation",
                description: "Grow your AI capabilities as your business expands with flexible solutions"
              },
              {
                title: "Enterprise Security",
                description: "Enterprise-grade security protocols for your data and operations"
              }
            ].map(({ title, description }) => (
              <div key={title} className="p-6">
                <h3 className="text-xl font-semibold mb-4">{title}</h3>
                <p className="text-gray-600">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Suspense fallback={<div className="h-40 bg-gray-100" />}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
